import { prunePersistedSession } from './cache';
import { makeScopeKey, normalizeScope } from './scope';
import type { AuthSession, PersistedAuthSessionV2 } from './types';

const STORAGE_KEYS = {
	v2: 'auth-session-v2',
	accessToken: 'accessToken',
	refreshToken: 'refreshToken',
	isSuperUser: 'isSuperUser',
	defaultCompanyId: 'defaultCompanyId',
	delegationsIds: 'delegationsIds',
} as const;

const EMPTY_SESSION: AuthSession = {
	accessToken: null,
	refreshToken: null,
	idToken: null,
	isSuperUser: false,
	isStaff: false,
	defaultCompanyId: null,
	delegationsIds: null,
};

const readLegacyNumber = (key: string): number | null => {
	const value = Number(localStorage.getItem(key));
	return Number.isSafeInteger(value) && value > 0 ? value : null;
};

const readLegacyDelegations = (): number[] => {
	try {
		const value: unknown = JSON.parse(localStorage.getItem(STORAGE_KEYS.delegationsIds) ?? '[]');
		return Array.isArray(value) ? value : [];
	} catch {
		return [];
	}
};

const removeLegacy = (): void => {
	localStorage.removeItem(STORAGE_KEYS.accessToken);
	localStorage.removeItem(STORAGE_KEYS.refreshToken);
	localStorage.removeItem(STORAGE_KEYS.isSuperUser);
	localStorage.removeItem(STORAGE_KEYS.defaultCompanyId);
	localStorage.removeItem(STORAGE_KEYS.delegationsIds);
};

export const writeV2 = (session: PersistedAuthSessionV2): void => {
	localStorage.setItem(STORAGE_KEYS.v2, JSON.stringify(session));
};

export const readV2 = (now = Date.now()): PersistedAuthSessionV2 | null => {
	const raw = localStorage.getItem(STORAGE_KEYS.v2);
	if (raw !== null) {
		try {
			const session = prunePersistedSession(JSON.parse(raw), now);
			if (session === null) {
				localStorage.removeItem(STORAGE_KEYS.v2);
				return null;
			}
			if (JSON.stringify(session) !== raw) writeV2(session);
			return session;
		} catch {
			localStorage.removeItem(STORAGE_KEYS.v2);
			return null;
		}
	}

	const accessToken = localStorage.getItem(STORAGE_KEYS.accessToken);
	const refreshToken = localStorage.getItem(STORAGE_KEYS.refreshToken);
	const scope = normalizeScope(
		readLegacyNumber(STORAGE_KEYS.defaultCompanyId),
		readLegacyDelegations(),
	);
	if (accessToken === null || refreshToken === null || scope === null) return null;

	const key = makeScopeKey(scope);
	const migrated: PersistedAuthSessionV2 = {
		version: 2,
		loginEpoch: 0,
		nextRevision: 2,
		isSuperUser: localStorage.getItem(STORAGE_KEYS.isSuperUser) === 'true',
		// The legacy (pre-token-claims) storage never carried an isStaff flag.
		isStaff: false,
		activeScopeKey: key,
		lastUsableScopeKey: key,
		entries: {
			[key]: {
				accessToken,
				refreshToken,
				companyId: scope.companyId,
				delegationIds: scope.delegationIds,
				revision: 1,
				acquiredAt: now,
				lastUsedAt: now,
				expiresAt: now + 7 * 24 * 60 * 60 * 1000,
			},
		},
	};

	writeV2(migrated);
	removeLegacy();
	return migrated;
};

export const read = (): AuthSession => {
	const session = readV2();
	if (session?.activeScopeKey === null || session === null) return EMPTY_SESSION;
	const entry = session.entries[session.activeScopeKey];
	if (!entry) return EMPTY_SESSION;

	return {
		accessToken: entry.accessToken,
		refreshToken: entry.refreshToken,
		idToken: null,
		isSuperUser: session.isSuperUser,
		isStaff: session.isStaff,
		defaultCompanyId: entry.companyId,
		delegationsIds: entry.delegationIds.length === 0 ? null : [...entry.delegationIds],
	};
};

export const clear = (): void => {
	localStorage.removeItem(STORAGE_KEYS.v2);
	removeLegacy();
};
